import type { LinkedItem, LinkedItemSon, TiradorWithPositions } from '@types'
import {
  getIndependentZocaloBodyLiftMm,
  mapApiFloorYThroughIndependentZocaloBodyMm,
  usesFreeDimensionScaling,
  usesPureGroupFreeDimensionScale,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'

type TiradorParent = Pick<
  LinkedItem,
  | 'tipoNormalizado'
  | 'hand'
  | 'width'
  | 'height'
  | 'thickness'
  | 'morphs'
  | 'childs'
  | 'catalogID'
  | 'freeDimensionCatalogMm'
  | 'freeDimensionGlbBaseMm'
  | 'hasIndependentZocalo'
  | 'independentZocaloHeightMm'
  | 'independentZocaloSonId'
  | 'description'
  | 'referencia'
>

type TiradorChild = TiradorWithPositions &
  Pick<LinkedItemSon, 'handPosition' | 'typeOfSon' | 'description' | 'id'> & {
    nodo?: number
    option?: number
  }

type MorphLike = {
  name: string
  value?: number
  default?: number
  _default?: number
}

/** Dimensión actual del morph: prioriza value (tras resize) sobre default. */
export function getMorphActualDimension(
  morph: MorphLike | undefined,
  parentFallback: number,
): number {
  return morph?.value ?? morph?.default ?? parentFallback
}

/** Dimensión base de referencia del morph (valor de catálogo). */
export function getMorphBaseDimension(
  morph: MorphLike | undefined,
  parentFallback: number,
): number {
  return morph?._default ?? parentFallback
}

/**
 * Free-dim / MBJ-COL: base = catálogo (tamaño al colocar, el de las posicionX/Y/Z API).
 * Nunca usar AABB GLB (p.ej. 400×700): desplaza tirador (z 581→481, x centrado mal).
 * Morph clásico: _default = base; default/value = actual.
 *
 * No usar storeMm como base: tras resize en free-dim, _default/store pueden
 * igualar el actual y Δ quedaría 0 → tirador sin recentrar.
 */
function getTiradorDimensionPair(
  morph: MorphLike | undefined,
  storeMm: number,
  catalogMm: number | undefined,
  usesFreeDim: boolean,
  _glbBaseMm?: number,
  preferCatalogBase = false,
): { base: number; actual: number } {
  if (usesFreeDim || preferCatalogBase) {
    const base =
      (catalogMm != null && catalogMm > 0 ? catalogMm : 0) ||
      (morph?._default != null && morph._default > 0 ? morph._default : 0) ||
      (morph?.default != null && morph.default > 0 ? morph.default : 0) ||
      storeMm
    const actual = storeMm > 0 ? storeMm : base
    return { base, actual }
  }
  return {
    base: getMorphBaseDimension(morph, storeMm),
    actual: getMorphActualDimension(morph, storeMm),
  }
}

/**
 * Índice de puerta para mano CN.
 * option = cajón/puerta; nodo API suele ser tipo de elemento (p.ej. 2 = tirador).
 * Preferir option (como en el resto del código).
 */
function getCnDoorIndex(childItem: { nodo?: number; option?: number }): number {
  return Math.max(0, Number(childItem.option ?? childItem.nodo ?? 1) - 1)
}

/**
 * MAL CN (2+ puertas): mantiene el offset API respecto al centro de CADA puerta.
 * (700→275/625; al ensanchar a 1700→525/1375, no +Δ/2 ni ratio global.)
 */
function getMalCnCenteredTiradorXMm(
  Xbase: number,
  widthBase: number,
  widthActual: number,
  childItem: { nodo?: number; option?: number },
): number {
  if (widthBase <= 0) return Xbase
  const doorIndex = getCnDoorIndex(childItem)
  const doorCount = Math.max(2, doorIndex + 1)
  const catalogDoorW = widthBase / doorCount
  const doorW = widthActual / doorCount
  const catalogDoorCenter = doorIndex * catalogDoorW + catalogDoorW / 2
  const pivotOffsetFromDoorCenter = Xbase - catalogDoorCenter
  return doorIndex * doorW + doorW / 2 + pivotOffsetFromDoorCenter
}

export function calculateTiradorPosition(
  childItem: TiradorChild,
  parentItem: TiradorParent,
): { x: number; y: number; z: number } {
  const usesFreeDim = usesFreeDimensionScaling(parentItem)
  const mbjColFree = usesPureGroupFreeDimensionScale(parentItem)
  const parentMorphs = parentItem.morphs || []
  const morphAncho = parentMorphs.find((m) => m.name === 'Ancho')
  const morphAlto = parentMorphs.find((m) => m.name === 'Alto')
  const morphFondo = parentMorphs.find((m) => m.name === 'Fondo')
  const catalog = parentItem.freeDimensionCatalogMm
  const glbBase = parentItem.freeDimensionGlbBaseMm

  const { base: widthBase, actual: widthActual } = getTiradorDimensionPair(
    morphAncho,
    parentItem.width,
    catalog?.x,
    usesFreeDim,
    glbBase?.x,
    mbjColFree,
  )
  const { base: heightBase, actual: heightActual } = getTiradorDimensionPair(
    morphAlto,
    parentItem.height,
    catalog?.y,
    usesFreeDim,
    glbBase?.y,
    mbjColFree,
  )
  const { base: depthBase, actual: depthActual } = getTiradorDimensionPair(
    morphFondo,
    parentItem.thickness,
    catalog?.z,
    usesFreeDim,
    glbBase?.z,
    mbjColFree,
  )
  let prefix = 'b'
  if (parentItem.tipoNormalizado === 'MAL' || parentItem.tipoNormalizado === 'COL') prefix = 'a'
  let Xbase: number | undefined
  let Ybase: number | undefined
  let Zbase: number | undefined
  const parentHand = parentItem.hand
  const isCentralHandNormal =
    parentHand === 'CN' &&
    (childItem.handPosition === 'Izquierda' || childItem.handPosition === 'Derecha')

  if (childItem.handPosition === 'Izquierda') {
    if (parentHand === 'CN') {
      Xbase = childItem[`${prefix}iPosicionX`]
      Ybase = childItem[`${prefix}iPosicionY`]
      Zbase = childItem[`${prefix}iPosicionZ`]
    } else if (isCentralHandNormal) {
      Xbase = childItem[`${prefix}dPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}dPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}dPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    } else {
      Xbase = childItem[`${prefix}iPosicionX`]
      Ybase = childItem[`${prefix}iPosicionY`]
      Zbase = childItem[`${prefix}iPosicionZ`]
    }
  } else if (childItem.handPosition === 'Centro') {
    Xbase = childItem[`${prefix}cPosicionX`]
    Ybase = childItem[`${prefix}cPosicionY`]
    Zbase = childItem[`${prefix}cPosicionZ`]
  } else if (childItem.handPosition === 'Derecha') {
    if (parentHand === 'CN') {
      Xbase = childItem[`${prefix}dPosicionX`]
      Ybase = childItem[`${prefix}dPosicionY`]
      Zbase = childItem[`${prefix}dPosicionZ`]
    } else if (isCentralHandNormal) {
      Xbase = childItem[`${prefix}iPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}iPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}iPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    } else {
      Xbase = childItem[`${prefix}dPosicionX`]
      Ybase = childItem[`${prefix}dPosicionY`]
      Zbase = childItem[`${prefix}dPosicionZ`]
    }
  } else if (childItem.handPosition === 'Normal') {
    const isFirstTirador =
      childItem ===
      Array.from(parentItem.childs || []).find((childID: unknown) => {
        const child = (parentItem as { items?: Map<string, TiradorChild> }).items?.get(
          childID as string,
        )
        return child && (child.description === 'Tirador' || child.typeOfSon === 'Tirador')
      })
    if (isFirstTirador) {
      Xbase = childItem[`${prefix}dPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}dPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}dPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    } else {
      Xbase = childItem[`${prefix}iPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}iPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}iPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    }
  }
  Xbase = Xbase ?? 0
  Ybase = Ybase ?? 0
  Zbase = Zbase ?? 0
  let x = Xbase
  let y = Ybase
  let z = Zbase

  if (parentItem.tipoNormalizado === 'MBJ') {
    // MBJ Centro:
    // - CN 2 puertas: misma idea que MAL (offset respecto al centro de CADA puerta).
    //   KBFB80702 @700: bc=75/425 (=175±100 y 525±100). option es 2/3 (no 1/2),
    //   así que el índice de puerta NO puede ser option−1 → se deduce por Xbase.
    // - 1 puerta / cajones: +Δ/2.
    if (childItem.handPosition === 'Centro') {
      if (parentHand === 'CN' && widthBase > 0) {
        const doorCount = 2
        const doorIndex = Xbase < widthBase / 2 ? 0 : 1
        const catalogDoorW = widthBase / doorCount
        const doorW = widthActual / doorCount
        const catalogDoorCenter = doorIndex * catalogDoorW + catalogDoorW / 2
        const pivotOffsetFromDoorCenter = Xbase - catalogDoorCenter
        x = doorIndex * doorW + doorW / 2 + pivotOffsetFromDoorCenter
      } else {
        x = Xbase + (widthActual - widthBase) / 2
      }
    } else if (childItem.handPosition === 'Derecha' && !isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase)
    } else if (isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase) / 2
    }
    // API Y ya es coords suelo (incl. hueco zócalo: bcY 891 con cuerpo 800).
    // mapApiFloorY escala solo el tramo sobre el zócalo; no sumar lift otra vez.
    if (getIndependentZocaloBodyLiftMm(parentItem) > 0 && (usesFreeDim || mbjColFree)) {
      y = mapApiFloorYThroughIndependentZocaloBodyMm(Ybase, parentItem)
    } else {
      y = Ybase - (heightBase - heightActual)
    }
    z = Zbase - (depthBase - depthActual)
  } else if (parentItem.tipoNormalizado === 'MAL') {
    // MAL Centro CN: offset respecto al centro de cada puerta.
    const isTiradorSon =
      childItem.typeOfSon === 'Tirador' || childItem.description === 'Tirador'
    if (
      isTiradorSon &&
      widthBase > 0 &&
      childItem.handPosition === 'Centro' &&
      parentHand === 'CN'
    ) {
      x = getMalCnCenteredTiradorXMm(Xbase, widthBase, widthActual, childItem)
    } else if (isTiradorSon && widthBase > 0 && childItem.handPosition === 'Centro') {
      x = Xbase + (widthActual - widthBase) / 2
    } else if (childItem.handPosition === 'Derecha' && !isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase)
    } else if (isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase) / 2
    } else if (widthBase > 0 && childItem.handPosition !== 'Izquierda') {
      x = (Xbase / widthBase) * widthActual
    }

    if (depthBase > 0 && depthActual !== depthBase) {
      z = (Zbase / depthBase) * depthActual
    } else if (depthActual !== depthBase) {
      z = Zbase - (depthBase - depthActual)
    }
    if (heightBase > 0 && heightActual !== heightBase) {
      y = (Ybase / heightBase) * heightActual
    }
  } else if (parentItem.tipoNormalizado === 'COL') {
    if (childItem.handPosition === 'Centro') {
      x = Xbase + (widthActual - widthBase) / 2
    } else if (childItem.handPosition === 'Derecha' && !isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase)
    } else if (isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase) / 2
    }

    if (depthActual !== depthBase) {
      z = Zbase - (depthBase - depthActual)
    }

    // Misma semántica que MBJ: posicionY API = suelo (cuerpo + zócalo).
    if (getIndependentZocaloBodyLiftMm(parentItem) > 0 && (usesFreeDim || mbjColFree)) {
      y = mapApiFloorYThroughIndependentZocaloBodyMm(Ybase, parentItem)
    } else if ((usesFreeDim || mbjColFree) && heightBase > 0 && heightActual !== heightBase) {
      y = Ybase * (heightActual / heightBase)
    }
  }

  return { x, y, z }
}
